import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatShortDate, calcVariance } from '../utils/dateUtils';
import { REACT_APP_API_URL } from '../config/env';
import { extractApiError } from '../types/apiError';
import { HubThemeScope } from './clients/hub/designSystem';

const API_BASE_URL = REACT_APP_API_URL;

interface DrillDownProps {
  open: boolean;
  onClose: () => void;
  metricId: number | null;
  metricName: string;
  week: string;
  unit?: string | null;
}

interface AptlyItem {
  sourceType?: string;
  sourceId: string;
  title: string;
  url: string;
  status: 'positive' | 'negative' | 'neutral';
  assignee: string | null;
  details: Record<string, any> | null;
}

interface ClockifyEntry {
  date: string;
  user: string;
  task: string;
  hours: number;
  description: string;
}

interface ClockifyProject {
  projectName: string;
  totalHours: number;
  entries: ClockifyEntry[];
}

interface DrillDownData {
  type: 'aptly' | 'native_ticket' | 'clockify' | 'clockify_ratio' | 'none';
  metric: { id: number; name: string; unit: string | null };
  week: string;
  items?: AptlyItem[];
  count?: number;
  projects?: ClockifyProject[];
  totalHours?: number;
  totalEntries?: number;
  summary?: { onCallHours: number; offCallHours: number; ratio: number };
  onCall?: ClockifyProject[];
  offCall?: ClockifyProject[];
  message?: string;
}

function formatWeekRange(mondayStr: string): string {
  const monday = new Date(mondayStr + 'T00:00:00');
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const start = monday.toLocaleDateString('en-US', opts);
  const end = sunday.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${start} – ${end}`;
}

// ─── Shared header cell style ──────────────────────────────────────────
const thSx = { fontWeight: 700, fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase' as const, letterSpacing: '0.05em' };

function escapeCsv(val: string | number | null | undefined): string {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export default function MetricDrillDownDialog({ open, onClose, metricId, metricName, week, unit: _unit }: DrillDownProps) {
  const [data, setData] = useState<DrillDownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !metricId || !week) return;
    setLoading(true);
    setError(null);
    setData(null);

    axios.get(`${API_BASE_URL}/metrics/${metricId}/drill-down`, { params: { week } })
      .then(res => setData(res.data?.data ?? res.data))  // 0b-02c: canonical { success, data } envelope
      .catch(err => setError(extractApiError(err, 'Failed to load drill-down data')))
      .finally(() => setLoading(false));
  }, [open, metricId, week]);

  return (
    <HubThemeScope>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          pb: 1, borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">{metricName}</Typography>
          <Typography variant="body2" color="text.secondary">{formatWeekRange(week)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {data && (data.type === 'aptly' || data.type === 'native_ticket' || data.type === 'clockify_ratio' || data.type === 'clockify') && (
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                const safeName = (metricName || 'metric').replace(/[^a-zA-Z0-9-_]/g, '_');
                let headers: string[];
                let rows: (string | number)[][];
                if ((data.type === 'aptly' || data.type === 'native_ticket') && data.items) {
                  const hasClient = data.items.some(i => i.details?.client);
                  const hasStage = data.items.some(i => i.details?.stage);
                  const hasDueDate = data.items.some(i => i.details?.dueDate);
                  const hasResolvedDate = data.items.some(i => i.details?.resolvedDate);
                  headers = ['Title', ...(hasClient ? ['Client'] : []), 'Assignee', ...(hasStage ? ['Stage'] : []), ...(hasDueDate ? ['Due'] : []), ...(hasResolvedDate ? ['Resolved'] : []), 'Status', ...(hasDueDate && hasResolvedDate ? ['Variance'] : [])];
                  rows = data.items.map(item => {
                    const v = calcVariance(item.details?.dueDate, item.details?.resolvedDate);
                    return [
                      item.title,
                      ...(hasClient ? [item.details?.client || ''] : []),
                      item.assignee || '',
                      ...(hasStage ? [item.details?.stage || ''] : []),
                      ...(hasDueDate ? [formatShortDate(item.details?.dueDate)] : []),
                      ...(hasResolvedDate ? [formatShortDate(item.details?.resolvedDate)] : []),
                      item.status,
                      ...(hasDueDate && hasResolvedDate ? [v?.label || ''] : []),
                    ];
                  });
                } else if (data.type === 'clockify_ratio' && (data.onCall || data.offCall)) {
                  headers = ['Category', 'Project', 'Date', 'User', 'Task', 'Hours', 'Description'];
                  rows = [];
                  (data.onCall || []).forEach(p => p.entries.forEach(e => rows.push(['On Call', p.projectName, e.date, e.user, e.task, e.hours, e.description || ''])));
                  (data.offCall || []).forEach(p => p.entries.forEach(e => rows.push(['Off Call', p.projectName, e.date, e.user, e.task, e.hours, e.description || ''])));
                } else if (data.type === 'clockify' && data.projects) {
                  headers = ['Project', 'Date', 'User', 'Task', 'Hours', 'Description'];
                  rows = [];
                  data.projects.forEach(p => p.entries.forEach(e => rows.push([p.projectName, e.date, e.user, e.task, e.hours, e.description || ''])));
                } else {
                  headers = [];
                  rows = [];
                }
                const csv = [headers.map(escapeCsv).join(','), ...rows.map(r => r.map(escapeCsv).join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `metric-drilldown-${safeName}-${week}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              Download CSV
            </Button>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, borderColor: 'divider' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: 'primary.main' }} /></Box>
        )}
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--status-off-track)' }}>{error}</Typography>
          </Box>
        )}
        {data && (data.type === 'aptly' || data.type === 'native_ticket') && <AptlyDrillDown data={data} />}
        {data && data.type === 'clockify_ratio' && <RatioDrillDown data={data} />}
        {/* Scorecard rebuild P5: time metrics render their REAL entries
            inline (the backend always returned them) — the old "available
            in Time Reports" punt is gone. */}
        {data && data.type === 'clockify' && <TimeEntriesDrillDown data={data} />}
        {data && data.type === 'none' && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{data.message}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </HubThemeScope>
  );
}

// ─── Time-entries Drill-Down (scorecard rebuild P5) ───────────────────
//
// Renders the per-project time entries the backend has always returned
// for time metrics — replacing the legacy "available in Time Reports"
// punt. Status colors route through the AUD-112 theme tokens.

function TimeEntriesDrillDown({ data }: { data: DrillDownData }) {
  const projects = data.projects || [];
  if (projects.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No time entries recorded this week.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Chip
          size="small"
          label={`${data.totalEntries ?? projects.reduce((s, p) => s + p.entries.length, 0)} entries · ${data.totalHours ?? 0}h total`}
          sx={{ bgcolor: 'var(--indigo-soft)', color: 'var(--indigo-on-soft)', fontWeight: 700 }}
        />
      </Box>
      {projects.map((p) => (
        <Box key={p.projectName} sx={{ mb: 2, border: '1px solid var(--rule)', borderRadius: '4px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: 'var(--paper-2)', borderBottom: '1px solid var(--rule)' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{p.projectName}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--indigo-on-soft)' }}>{p.totalHours}h</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Person</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Task</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Hours</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {p.entries.map((e, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{e.date}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>{e.user}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }} title={e.description || undefined}>{e.task}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{e.hours}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
}

// ─── Aptly Drill-Down ──────────────────────────────────────────────────

function AptlyDrillDown({ data }: { data: DrillDownData }) {
  const navigate = useNavigate();
  const items = data.items || [];
  const positiveCount = items.filter(i => i.status === 'positive').length;
  const negativeCount = items.filter(i => i.status === 'negative').length;

  // Detect which optional columns are present
  const hasClient = items.some(i => i.details?.client);
  const hasStage = items.some(i => i.details?.stage);
  const hasDueDate = items.some(i => i.details?.dueDate);
  const hasResolvedDate = items.some(i => i.details?.resolvedDate);
  const hasVariance = hasDueDate && hasResolvedDate; // show variance when we have both dates
  const hasStatusCol = positiveCount > 0 || negativeCount > 0;

  return (
    <Box>
      {/* Summary bar */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Chip
          label={`${items.length} items`}
          size="small"
          sx={{ bgcolor: 'var(--indigo-soft)', color: 'var(--indigo-on-soft)', fontWeight: 600, fontSize: '0.75rem', height: 24 }}
        />
        {positiveCount > 0 && (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label={`${positiveCount} on time`}
            size="small"
            sx={{ bgcolor: 'var(--status-on-track-soft)', color: 'var(--status-on-track-on-soft)', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: 'var(--status-on-track-on-soft)' } }}
          />
        )}
        {negativeCount > 0 && (
          <Chip
            icon={<CancelIcon sx={{ fontSize: 14 }} />}
            label={`${negativeCount} late`}
            size="small"
            sx={{ bgcolor: 'var(--status-off-track-soft)', color: 'var(--status-off-track-on-soft)', fontWeight: 500, fontSize: '0.72rem', height: 24, '& .MuiChip-icon': { color: 'var(--status-off-track-on-soft)' } }}
          />
        )}
        {/* No extra neutral chip — total count is already shown in the gold chip */}
      </Box>

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { borderBottom: '1px solid', borderColor: 'divider', py: 1 } }}>
              <TableCell sx={{ ...thSx }}>Title</TableCell>
              {hasClient && <TableCell sx={{ ...thSx, width: 150 }}>Client</TableCell>}
              <TableCell sx={{ ...thSx, width: 120 }}>Assignee</TableCell>
              {hasStage && <TableCell sx={{ ...thSx, width: 120 }}>Stage</TableCell>}
              {hasDueDate && <TableCell sx={{ ...thSx, width: 80, textAlign: 'center' }}>Due</TableCell>}
              {hasResolvedDate && <TableCell sx={{ ...thSx, width: 80, textAlign: 'center' }}>Resolved</TableCell>}
              {hasStatusCol && <TableCell sx={{ ...thSx, width: 80, textAlign: 'center' }}>Status</TableCell>}
              {hasVariance && <TableCell sx={{ ...thSx, width: 100, textAlign: 'center' }}>Variance</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => {
              // Tickets: always the canonical /operations/tickets/:id route
              // (opens the HubDialog modal) — even for drill rows stored
              // before the collectors switched off /helpdesk?thread=.
              const ticketMatch = /^ticket:(\d+)$/.exec(item.sourceId || '');
              const itemHref = ticketMatch ? `/operations/tickets/${ticketMatch[1]}` : item.url;
              return (
                <TableRow
                  key={item.sourceId || idx}
                  sx={{
                    borderLeft: item.status !== 'neutral' ? `3px solid ${item.status === 'positive' ? 'var(--status-on-track)' : 'var(--status-off-track)'}` : undefined,
                    '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 1 },
                    '&:hover': { bgcolor: 'rgba(var(--overlay-rgb),0.03)' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  {/* Title */}
                  <TableCell>
                    {itemHref ? (
                      itemHref.startsWith('/') ? (
                        <Link
                          component="button"
                          underline="hover"
                          onClick={() => navigate(itemHref)}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            fontWeight: 500, fontSize: '0.8rem', border: 'none', background: 'none', cursor: 'pointer',
                            color: 'primary.light',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {item.title}
                          <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.35 }} />
                        </Link>
                      ) : (
                        <Link
                          href={itemHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            fontWeight: 500, fontSize: '0.8rem',
                            color: 'primary.light',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {item.title}
                          <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.35 }} />
                        </Link>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.primary' }}>{item.title}</Typography>
                    )}
                  </TableCell>

                  {/* Client */}
                  {hasClient && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: item.details?.client ? 'text.primary' : 'text.secondary' }}>
                        {item.details?.client || '—'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Assignee */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{item.assignee || '—'}</Typography>
                  </TableCell>

                  {/* Stage */}
                  {hasStage && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: item.details?.stage ? 'text.primary' : 'text.secondary' }}>
                        {item.details?.stage || '—'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Due Date */}
                  {hasDueDate && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {formatShortDate(item.details?.dueDate)}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Completed Date */}
                  {hasResolvedDate && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {formatShortDate(item.details?.resolvedDate)}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Status */}
                  {hasStatusCol && (
                    <TableCell sx={{ textAlign: 'center' }}>
                      {item.status === 'positive' && (
                        <Chip label="On Time" size="small" sx={{ bgcolor: 'var(--status-on-track-soft)', color: 'var(--status-on-track-on-soft)', fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                      )}
                      {item.status === 'negative' && (
                        <Chip label="Late" size="small" sx={{ bgcolor: 'var(--status-off-track-soft)', color: 'var(--status-off-track-on-soft)', fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                      )}
                      {item.status === 'neutral' && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>—</Typography>
                      )}
                    </TableCell>
                  )}

                  {/* Variance (days between due and resolved) */}
                  {hasVariance && (() => {
                    const v = calcVariance(item.details?.dueDate, item.details?.resolvedDate);
                    return (
                      <TableCell sx={{ textAlign: 'center' }}>
                        {v ? (
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: v.color }}>
                            {v.label}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>—</Typography>
                        )}
                      </TableCell>
                    );
                  })()}
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary', borderBottom: 'none' }}>
                  No items found for this week
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Ratio Drill-Down (On Call vs Off Call) ────────────────────────────

function RatioDrillDown({ data }: { data: DrillDownData }) {
  const { summary, onCall = [], offCall = [] } = data;
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandSignal, setExpandSignal] = useState(0);

  const handleExpandAll = () => { setAllExpanded(true); setExpandSignal(s => s + 1); };
  const handleCollapseAll = () => { setAllExpanded(false); setExpandSignal(s => s + 1); };

  return (
    <Box>
      {summary && (
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
          <Chip label={`On Call: ${summary.onCallHours}h`} size="small" sx={{ bgcolor: 'var(--status-warning-soft)', color: 'var(--status-warning-on-soft)', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
          <Chip label={`Off Call: ${summary.offCallHours}h`} size="small" sx={{ bgcolor: 'var(--status-neutral-soft)', color: 'var(--status-neutral-on-soft)', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
          <Chip
            label={`Ratio: ${summary.ratio.toFixed(3)}`}
            size="small"
            sx={{
              bgcolor: summary.ratio <= 0.333 ? 'var(--status-on-track-soft)' : 'var(--status-off-track-soft)',
              color: summary.ratio <= 0.333 ? 'var(--status-on-track-on-soft)' : 'var(--status-off-track-on-soft)',
              fontWeight: 700, fontSize: '0.75rem', height: 24,
            }}
          />
          <Typography variant="caption" color="text.secondary">(target: 0.333 = 1:3)</Typography>
          <Box sx={{ flex: 1 }} />
          <ExpandCollapseButtons onExpandAll={handleExpandAll} onCollapseAll={handleCollapseAll} />
        </Box>
      )}

      <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: 'var(--status-warning)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
          On Call Entries
        </Typography>
      </Box>
      {onCall.map((proj, idx) => <ProjectGroup key={`on-${idx}`} project={proj} forceExpanded={allExpanded} signal={expandSignal} />)}
      {onCall.length === 0 && (
        <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>No on-call entries this week</Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: 'divider' }} />

      <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color: 'var(--status-neutral)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
          Off Call Entries
        </Typography>
      </Box>
      {offCall.map((proj, idx) => <ProjectGroup key={`off-${idx}`} project={proj} forceExpanded={allExpanded} signal={expandSignal} />)}
      {offCall.length === 0 && (
        <Box sx={{ px: 2.5, pb: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>No off-call entries this week</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Expand / Collapse All buttons ─────────────────────────────────────

function ExpandCollapseButtons({ onExpandAll, onCollapseAll }: { onExpandAll: () => void; onCollapseAll: () => void }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Button
        size="small"
        startIcon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
        onClick={onExpandAll}
        sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.primary', bgcolor: 'rgba(var(--overlay-rgb),0.05)' } }}
      >
        Expand all
      </Button>
      <Button
        size="small"
        startIcon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
        onClick={onCollapseAll}
        sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.primary', bgcolor: 'rgba(var(--overlay-rgb),0.05)' } }}
      >
        Collapse all
      </Button>
    </Box>
  );
}

// ─── Shared: Expandable Project Group ──────────────────────────────────

function ProjectGroup({ project, forceExpanded, signal }: { project: ClockifyProject; forceExpanded?: boolean; signal?: number }) {
  const [expanded, setExpanded] = useState(false);

  // React to parent expand/collapse signals
  useEffect(() => {
    if (signal !== undefined && signal > 0) {
      setExpanded(!!forceExpanded);
    }
  }, [signal, forceExpanded]);

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(var(--overlay-rgb),0.03)' },
          transition: 'background-color 0.15s',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {expanded
            ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          }
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', color: 'text.primary' }}>{project.projectName}</Typography>
        </Box>
        <Chip
          label={`${project.totalHours}h`}
          size="small"
          sx={{ bgcolor: 'rgba(var(--overlay-rgb),0.06)', color: 'text.secondary', fontWeight: 600, fontSize: '0.72rem', height: 22 }}
        />
      </Box>
      <Collapse in={expanded}>
        <TableContainer sx={{ pl: 4, pr: 1, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.8 } }}>
                <TableCell sx={{ ...thSx }}>Date</TableCell>
                <TableCell sx={{ ...thSx }}>User</TableCell>
                <TableCell sx={{ ...thSx }}>Task</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Hours</TableCell>
                <TableCell sx={{ ...thSx }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project.entries.map((entry, idx) => (
                <TableRow key={idx} sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider', py: 0.8 }, '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: 'rgba(var(--overlay-rgb),0.02)' } }}>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.primary' }}>{entry.date}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{entry.user}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{entry.task}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', fontWeight: 600, color: 'primary.main' }}>{entry.hours}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.description || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}
